/**
 * Cycle detection over a workflow graph.
 *
 * Lives in its own leaf module — importing only `./types` — to keep the package
 * acyclic. `expressions.ts` needs `detectCycles`, and `graph-validation.ts` now
 * reaches `expressions.ts` transitively via `fanout-topology.ts`. Keeping these
 * two functions in `graph-validation.ts` closed that loop:
 *
 *   expressions -> graph-validation -> fanout-topology -> expressions
 *
 * Circular imports risk a binding being undefined at module-eval time depending
 * on which entry point loads first, so the repo treats any cycle outside
 * `*.model.ts` as a defect.
 */

import type {
  CycleDetectionResult,
  ValidationEdge,
  ValidationStep,
} from "./types";

/**
 * Build an adjacency list from edges for efficient traversal
 *
 * @param edges - All edges in the graph
 * @returns Map of source node ID to array of target node IDs
 */
export function buildAdjacencyList(
  edges: Array<Pick<ValidationEdge, "sourceStepId" | "targetStepId">>,
): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();

  for (const edge of edges) {
    const targets = adjacency.get(edge.sourceStepId) ?? [];
    targets.push(edge.targetStepId);
    adjacency.set(edge.sourceStepId, targets);
  }

  return adjacency;
}

/**
 * Detect cycles in the workflow graph using DFS with a recursion stack
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Result indicating if a cycle exists and which nodes are involved
 */
export function detectCycles(
  steps: Array<Pick<ValidationStep, "id">>,
  edges: Array<Pick<ValidationEdge, "sourceStepId" | "targetStepId">>,
): CycleDetectionResult {
  const adjacency = buildAdjacencyList(edges);
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const parent = new Map<string, string>();

  // DFS to detect cycle
  function dfs(nodeId: string): string | null {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacency.get(nodeId) ?? [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        parent.set(neighbor, nodeId);
        const cycleStart = dfs(neighbor);
        if (cycleStart !== null) {
          return cycleStart;
        }
      } else if (recursionStack.has(neighbor)) {
        // Found a cycle - return the node where the cycle starts
        parent.set(neighbor, nodeId);
        return neighbor;
      }
    }

    recursionStack.delete(nodeId);
    return null;
  }

  // Check all nodes (graph may be disconnected)
  for (const step of steps) {
    if (!visited.has(step.id)) {
      const cycleStart = dfs(step.id);
      if (cycleStart !== null) {
        // Reconstruct the cycle path
        const cycleNodeIds: string[] = [cycleStart];
        let current = parent.get(cycleStart);
        while (current && current !== cycleStart) {
          cycleNodeIds.push(current);
          current = parent.get(current);
        }
        cycleNodeIds.reverse();

        return {
          hasCycle: true,
          cycleNodeIds,
        };
      }
    }
  }

  return { hasCycle: false };
}
